import { isRegulatedProfession } from "./profession.data";
import { isNil } from "lodash";

/**
 * Whether a maskable field APPLIES to a candidate at all, independent of any mask. An internal mask is scoped to a user group while some
 * fields and two tabs render only for certain CANDIDATES, so a mandatory flag on such a field makes the form unsatisfiable for every other
 * candidate: required, invisible, and nothing says which.
 *
 * Only the INTERPRETER lives here; the declarations sit on `@IEProperty`'s `profileTab.appliesWhen` (whole tabs: `TAB_APPLICABILITY`) and
 * reach the frontend over the field catalog. Shared because both sides evaluate: the frontend against the live form (`applyMaskToForm`)
 * and the backend against the candidate document (`CandidateService`'s required-field check).
 *
 * These rules only ever suppress MANDATORY. A field hidden here is already not rendered, and disabling its control would drop the stored
 * value from `getRawValue()` (which several widgets rely on) and collide with the one-way `lockControlDisabled`.
 */

/** Named predicates for a condition that is a real computation, not a value comparison. A declaration carries the NAME, so the
 *  implementation stays in one place, both sides evaluate identically and the declaration stays serializable. */
export const FieldApplicabilityPredicates = {
  isRegulatedProfession: (value: unknown): boolean => typeof value === "string" && isRegulatedProfession(value),
  /** Reads a BOOLEAN gate, since `in`/`notIn` compare strings and `notIn: ["false"]` would never match `false`. Strict about `true`: an untouched checkbox is `null`, already answered by `whenAbsent` before any predicate runs. */
  isTrue: (value: unknown): boolean => value === true,
} as const;

export type FieldApplicabilityPredicate = keyof typeof FieldApplicabilityPredicates;

/**
 * One applicability rule: read `field`'s value, then decide. `field` is a `"tabKey|fieldName"` key like `profileTab.requires`. A field of
 * the SAME tab is looked up on the control's own parent group, which makes it work for a tab rendered more than once (the recognition
 * path's `current`/`alternative`, each with its own role); any other tab (in practice `header`) resolves at the candidate root.
 */
export interface FieldApplicability {
  field: string;
  /** Applies when the value is one of these. String comparison; a boolean gate uses `predicate: "isTrue"`. */
  in?: string[];
  /** Applies when the value is NOT one of these. String comparison, like {@link FieldApplicability.in}. */
  notIn?: string[];
  /** Applies when this named predicate holds (combine with {@link negate} for the inverse). */
  predicate?: FieldApplicabilityPredicate;
  negate?: boolean;
  /**
   * Read `field` from the nearest ENCLOSING group that has it, instead of the control's own parent: for a gate that sits above the group
   * the governed control lives in (`hasDifferentRelationships` on `employmentRelationship`, the control in its `afterRecognition` child).
   */
  searchAncestors?: boolean;
  /**
   * How to read a MISSING value at `field`. Default `"notApplicable"`, which can never invent an unsatisfiable requirement (at worst a
   * mandatory rule is dropped). `"applicable"` is needed where the template does the same: `fullEquivalenceReceived` sits in the `@else` of
   * `@if (isRegulatedProfession)`, and `isRegulatedProfession(null)` is `false`, so an unknown profession RENDERS it.
   */
  whenAbsent?: "applicable" | "notApplicable";
  /**
   * Restricts the rule to ONE immigration country. The catalog collapses a field name declared by several countries' models into ONE row
   * (first-wins in `getLabelsForTab`): DE and AT both declare `federalState`, `fileNumber`, `pathOfRecognition` and all 18 `vocationalSchool`
   * fields. A gate only one template has (DE hides the vocational school for non-trainees, AT does not) would otherwise drop mandatory for the
   * other country's candidates, where the field is on screen.
   *
   * A country code as `toRealImmigrationCountry` handles them, not the generated enum. Applied by the CALLER ({@link isRuleInScope}), since
   * the candidate's country is not the value the rule reads and both callers already hold it.
   */
  onlyFor?: string;

  /**
   * Translation key naming the condition in the admin's words ("nur für Azubis"), shown by the mask editor beside a field just marked
   * MANDATORY. Without it the requirement is dropped silently and the admin never learns the configuration was partly ineffective, so treat
   * it as required in practice.
   */
  description?: string;
}

/** Whether a rule has anything to say for a candidate in `immigrationCountry` ({@link FieldApplicability.onlyFor}). Fails OPEN on an unknown country, so an unresolved country never drops a requirement it should keep. */
export function isRuleInScope(rule: FieldApplicability, immigrationCountry: string | null | undefined): boolean {
  return isNil(rule.onlyFor) || isNil(immigrationCountry) || rule.onlyFor === immigrationCountry;
}

/** Evaluates one rule against the value already read from `rule.field`. Pure: the lookup needs a live form on the frontend and a candidate document on the backend, so it stays with the caller. */
export function isApplicableForValue(rule: FieldApplicability, value: unknown): boolean {
  if (isNil(value) || value === "") {
    return rule.whenAbsent === "applicable";
  }

  if (!isNil(rule.predicate)) {
    const holds = FieldApplicabilityPredicates[rule.predicate](value);
    return rule.negate ? !holds : holds;
  }

  // String comparisons by design (static-data keys, roles). A boolean gate uses `isTrue`: comparing `false` with `"false"` would read as
  // "applicable" and put the requirement back on a hidden field.
  if (!isNil(rule.in)) {
    return typeof value === "string" && rule.in.includes(value);
  }

  if (!isNil(rule.notIn)) {
    return typeof value === "string" && !rule.notIn.includes(value);
  }

  // A rule with only a `field` is a mistake, but failing open keeps it from making an existing mandatory field unsatisfiable.
  return true;
}
